import { useGetPage } from 'hooks/useGetPage';
import { pageIds } from 'utilities/constants';
import { ReactComponent as Edit } from 'assets/icons/edit-blue.svg';
import {
  CustomClassNamesProps,
  KeyMapProps,
} from 'components/codexTable/interfaces/keymap.interfaces';
import { Order_By, ProductOutput } from 'graphql/generated/hasura';
import React from 'react';
import { formatDateForAppointment } from 'utilities/functions';
import { twMerge } from 'tailwind-merge';
import ButtonComponent from 'components/button/buttonComponent';
import { SUPER_ADMIN_PRODUCTS } from 'utilities/routes';
import { useNavigate } from 'react-router-dom';

export enum ProductCategory {
  Test_Kits = 1,
  Supplements,
  Topical,
}

export const CategoryLabels = {
  [ProductCategory.Supplements]: 'Supplements',
  [ProductCategory.Topical]: 'Topical',
  [ProductCategory.Test_Kits]: 'Test kit',
};

const columns = [
  'sku',
  'category',
  'created_at',
  'price',
  'availability',
  'actions',
] as const;
export const FILTER_BLOCK_ID = 'product-filter';
const excludedSortColumns = [
  'sku',
  'category',
  'price',
  'availability',
  'actions',
];
export type ColumnKey = (typeof columns)[number];
type SortableColumn = Exclude<ColumnKey, 'actions'>;

export type My_Orders_Products_By = Partial<Record<SortableColumn, Order_By>>;

export const inStockLabels = new Map([
  [true, 'Active'],
  [false, 'Inactive'],
]);

export const inStockValues = new Map([
  ['Active', true],
  ['Inactive', false],
]);

const inStockClassName = new Map([
  [true, 'text-bright-green border-bright-green bg-green-50'],
  [false, 'text-med-gray-3 border-med-gray-3 bg-base-content'],
]);

export const useGetTableConfig = () => {
  const navigate = useNavigate();
  const { data: locale, loading: localeLoading } = useGetPage({
    locale: 'en',
    pageId: pageIds.SUPER_ADMIN_PRODUCTS,
  });

  const customClassesDesktop: CustomClassNamesProps = {
    sku: {
      customRowClassName:
        'pl-2 text-left min-w-[150px] w-[9%] align-top font-medium text-med-gray-3',
      spanClass: 'pl-2',
    },
    category: {
      customRowClassName:
        'text-left min-w-[500px] w-[27%] align-top font-medium break-all text-med-gray-3',
      spanClass: 'pl-0',
    },
    created_at: {
      customRowClassName:
        'pl-2 text-left min-w-[250px] w-[27%] align-top font-medium text-med-gray-3',
      spanClass: 'pl-0',
    },
    price: {
      customRowClassName:
        'pl-2 text-left min-w-[120px] w-[15%] align-top font-medium text-med-gray-3',
      spanClass: 'pl-0',
    },
    availability: {
      customRowClassName: 'px-0 align-top w-[15%] font-medium min-w-[180px]',
      spanClass: 'pl-0',
    },
    actions: {
      customRowClassName: 'px-2.5 align-top min-w-[120px] w-[9%]',
      spanClass: 'pl-2',
    },
  };
  const keyMap: KeyMapProps = {
    sku: {
      column: 'sku',
      isActive: () => false,
      direction: () => Order_By.Desc,
      FormatValue: ({ entry }) => {
        const product = entry as ProductOutput;
        return (
          <div className="text-left font-medium text-med-gray-3">
            {product.sku ?? '-'}
          </div>
        );
      },
    },
    category: {
      column: 'category',
      isActive: () => false,
      direction: () => Order_By.Desc,
      FormatValue: ({ entry }) => {
        const product = entry as ProductOutput;
        const label = CategoryLabels[product.category as ProductCategory];
        return (
          <div className="text-left font-medium text-med-gray-3">{label}</div>
        );
      },
    },
    created_at: {
      column: 'created_at',
      isActive: (orderBy) => !!orderBy.created_at,
      direction: (orderBy: My_Orders_Products_By) =>
        orderBy.created_at || Order_By.Desc,
      FormatValue: ({ entry }) => {
        const product = entry as ProductOutput;
        const result = formatDateForAppointment(product.created_at, {
          simplified: true,
          withTimeZone: true,
        });
        return (
          <div className="text-left font-medium text-med-gray-3">
            {result ?? ''}
          </div>
        );
      },
    },
    price: {
      column: 'price',
      isActive: () => false,
      direction: () => Order_By.Desc,
      FormatValue: ({ entry }) => {
        const product = entry as ProductOutput;
        return (
          <div className="text-left font-medium text-med-gray-3">
            $ {product.price ?? ''}
          </div>
        );
      },
    },
    availability: {
      column: 'availability',
      isActive: () => false,
      direction: () => Order_By.Desc,
      FormatValue: ({ entry }) => {
        const product = entry as ProductOutput;
        const label = inStockLabels.get(product.is_shipmonk_active);
        const className = inStockClassName.get(product.is_shipmonk_active);
        return (
          <p
            className={twMerge(
              'border rounded-xl px-2.5 font-bold text-h7 whitespace-nowrap',
              'font-semibold text-sm w-fit',
              className,
            )}
          >
            {label}
          </p>
        );
      },
    },
    actions: {
      column: 'actions',
      isActive: () => false,
      direction: () => Order_By.Desc,
      FormatValue: ({ entry }) => {
        const product = entry as ProductOutput;

        return (
          <div className="flex gap-2.5 items-center">
            <ButtonComponent
              testID="edit"
              iconPosition="left"
              containerClassName="!gap-1"
              type="underline"
              onClick={() => navigate(`${SUPER_ADMIN_PRODUCTS}/${product.id}`)}
              Icon={() => <Edit className="w-5 h-5 fill-[#00149C]" />}
            >
              Edit
            </ButtonComponent>
          </div>
        );
      },
    },
  };

  return {
    locale,
    keyMap,
    columns,
    excludedSortColumns,
    localeLoading,
    customClassesDesktop,
    inStockLabels,
    inStockClassName,
  };
};
